import Box from '@material-ui/core/Box';
import Button, { ButtonProps } from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Fade from '@material-ui/core/Fade';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';

interface BaseProps {
  loading?: boolean;
  label: string;
}

type SubmitButtonProps = ButtonProps<'button', BaseProps>;

// Based on 'button', the element MUI actually renders when no href is given,
// plus the anchor props this component forwards. It was `ButtonProps<any, ...>`,
// which widened tabIndex to `string | number` and broke the
// `component={RouterLink}` overload; 'a' is wrong the other way, since it puts
// anchor-only attributes on the plain-button branch.
type LinkButtonProps = ButtonProps<'button', BaseProps> & {
  to?: string;
  href?: string;
  target?: string;
  rel?: string;
};

const defaultProps: Pick<
  SubmitButtonProps,
  'type' | 'color' | 'variant' | 'size'
> = {
  type: 'submit',
  color: 'primary',
  variant: 'contained',
  size: 'large',
};

const ButtonContent: React.FC<BaseProps & { left?: boolean }> = ({
  loading,
  label,
  left,
  children,
}) => (
  <>
    <Fade in={!loading}>
      <Box style={{ textAlign: 'center' }}>
        {children}
        {label && <FormattedMessage id={label} />}
      </Box>
    </Fade>
    {loading && (
      <Fade in={loading}>
        <Box
          position={'absolute'}
          height={'100%'}
          display={'flex'}
          alignItems={'center'}
          left={left && 4}
        >
          <CircularProgress color="inherit" size={24} />
        </Box>
      </Fade>
    )}
  </>
);

export const SubmitButton: React.FC<SubmitButtonProps> = React.memo(
  ({ label, loading, ...rest }) => (
    <Button {...defaultProps} disabled={loading || rest.disabled} {...rest}>
      <ButtonContent label={label} loading={loading} />
    </Button>
  ),
);

export const LinkButton: React.FC<LinkButtonProps> = React.memo(
  // `href` is pulled out of the rest so the spread cannot carry an optional
  // one; MUI's href overload wants `string`. Callers pass either `to` (internal
  // route) or `href` (a document or an external link), and `href` used to win
  // by spread order -- `href ?? to` keeps that.
  ({ label, loading, to, children, href, ...rest }) => {
    const props = {
      ...defaultProps,
      disabled: loading || rest.disabled,
      ...rest,
    };
    const content = (
      <ButtonContent label={label} loading={loading} left>
        {children}
      </ButtonContent>
    );

    const linkHref = href ?? to;

    if (rest.target || !to) {
      return linkHref ? (
        <Button {...props} href={linkHref} style={{ position: 'relative' }}>
          {content}
        </Button>
      ) : (
        <Button {...props} style={{ position: 'relative' }}>
          {content}
        </Button>
      );
    }

    return (
      // `component` goes after the spread so MUI can pick its overload from it.
      // The cast is the one thing this component's props cannot express: the
      // same object serves a <button> (no `to`) and RouterLink's <a>, and MUI
      // types every event handler against a specific element, so
      // ClipboardEventHandler<HTMLButtonElement> is rejected here. Only the
      // handler element types differ; the values are identical.
      <Button
        {...(props as ButtonProps<'a', BaseProps>)}
        to={to}
        component={RouterLink}
        style={{ position: 'relative' }}
      >
        {content}
      </Button>
    );
  },
);
